// modules/asset/frontend/routes.tsx
//
// Module-owned dashboard route slice. First module to ship frontend
// pages alongside its backend API + AI tools + RBAC scopes (G
// frontend aggregator, follow-up to E #613).
//
// The host SPA (apps/frontend/src/routes/dashboard/Shell.tsx) imports
// this `ROUTES` array and spreads its entries into the dashboard's
// <Routes> tree. Each entry carries `permissions` (verified by
// RequireRole at runtime — the asset:read.list scope is a module-
// owned scope discovered via the F #612 scope aggregator).
//
// `path` is relative to `/dashboard/`, matching the convention used
// in Shell.tsx (e.g. `path="assets"` becomes `/dashboard/assets`).

import React from "react";
import { Boxes } from "lucide-react";
import type { ModuleNavItem } from "@nexus/frontend-shared/nav";
import AssetList from "./pages/AssetList.js";
import AssetDetail from "./pages/AssetDetail.js";

export interface ModuleDashboardRoute {
  /** Path RELATIVE to /dashboard/. e.g. "assets" → /dashboard/assets. */
  path: string;
  /** React element to render inside RequireRole gate. */
  element: React.ReactNode;
  /** Required permissions for access. ANY match grants access; empty
   *  array = any authenticated session. Convention mirrors
   *  apps/frontend/src/dashboard/auth/RequireRole.tsx. */
  permissions: readonly string[];
}

export const ROUTES: ModuleDashboardRoute[] = [
  {
    path: "assets",
    element: <AssetList />,
    permissions: ["asset:read.list"],
  },
  {
    // Detail / edit page. Edit requires asset:write.update but the
    // route gate uses :read.list (anyone with list permission can
    // *open* the detail page; the Save button's PATCH is gated
    // server-side by :write.update). Treating "open detail" as a
    // read action keeps the URL link-shareable without permission-
    // gating the entire route on the writer scope.
    path: "assets/:id",
    element: <AssetDetail />,
    permissions: ["asset:read.list"],
  },
];

/** Sidebar nav contributions. Host SPA aggregates these into a
 *  "模組" group in apps/frontend/src/dashboard/lib/nav.ts. Detail
 *  page isn't surfaced — list is the canonical entry point; detail
 *  is reached by clicking a row. */
export const NAV_ITEMS: ModuleNavItem[] = [
  {
    label: "資產盤點",
    to: "/dashboard/assets",
    icon: Boxes,
    permissions: ["asset:read.list"],
  },
];
